/**
 * エラー詳細 CSV — TWO paths, one button.
 *
 * downloadErrorCsv builds the file in the browser from what the row already holds. It serves the tabs that
 * still run on sample data, where there is no server to ask.
 *
 * downloadRunErrorCsv (bottom of this file) streams it from the API for a live run. That is the one that
 * matters for レセプト同期: a run's row carries one summary sentence, while the server can emit one line per
 * failed rezept per attempt out of sync_audit_logs.
 */
import dayjs from 'dayjs';
import { apiClient } from '@/shared/lib/axios';
import { downloadBlob, parseFilenameFromHeader } from '@/utils/blobDownload';
import { PATIENT_COLUMN, type NotificationItem } from '@/types/admin-notification-center';

/**
 * 発生日時 in the file is a LOCAL, readable timestamp — not the ISO/UTC string the API speaks.
 *
 * The file is opened in Excel by someone comparing it against a screen that shows 令和08年08月27日 09:15.
 * 「2026-08-27T00:15:00.000Z」 is the same instant nine hours earlier by the reader's clock, which is exactly
 * the kind of quiet mismatch that turns a support call into an argument.
 */
const localTime = (iso: string): string => (iso ? dayjs(iso).format('YYYY/MM/DD HH:mm:ss') : '');

/** RFC 4180 quoting. A 内容 sentence can contain 「、」 and quotes; a 理由 can contain a comma. */
const cell = (value: unknown): string => {
  const text = value == null ? '' : String(value);
  return `"${text.replace(/"/g, '""')}"`;
};

const HEADERS = [
  '通知ID',
  // 試行回数 has no column on ANY tab any more (owner's decision, 2026-08-27). It is still worth having
  // when someone is actually diagnosing a failure — 「it failed once」 and 「it failed four times」 are
  // different problems — so it moves here rather than disappearing from the product altogether.
  '試行回数',
  '失敗件数',
  '発生日時',
  'エラーコード',
  'HTTPステータス',
  '送信先',
  '治療院ID',
  '治療院名',
  // Same two words as the columns and the search label, never a fourth spelling: this file gets pasted into
  // a ticket next to a screenshot of the table, and the two have to be readable as the same thing.
  PATIENT_COLUMN.SERIAL,
  PATIENT_COLUMN.NAME,
  '提出年月',
  'キー',
  '項目',
  '値',
  '理由',
  'メッセージ',
];

const buildRows = (item: NotificationItem): string[][] => {
  const detail = item.errorDetail;
  const head = [
    String(item.id),
    String(item.attempts),
    // Blank rather than 0 on the tabs where a row is one record: 「0 failures」 on a row that IS the failure
    // reads as a contradiction.
    item.failureCount == null ? '' : String(item.failureCount),
    localTime(item.occurredAt),
    detail?.code ?? '',
    detail?.httpStatus == null ? '' : String(detail.httpStatus),
    detail?.endpoint ?? '',
    item.clinicNumber ?? '',
    item.clinicName ?? '',
    item.patientSerial ?? '',
    item.patientName ?? '',
    item.submitYearMonth ?? '',
    item.targetKey,
  ];

  // No per-field rows (a timeout has none) still gets ONE line, so the file is never just a header.
  if (!detail || detail.rows.length === 0) {
    return [[...head, '', '', '', detail?.message ?? item.summary]];
  }

  return detail.rows.map(row => [...head, row.field, row.value, row.reason, detail.message]);
};

export const buildErrorCsv = (items: NotificationItem[]): string => {
  const lines = [HEADERS, ...items.flatMap(buildRows)];
  return lines.map(cols => cols.map(cell).join(',')).join('\r\n');
};

/**
 * Excel on Windows is what these files get opened in, and without a UTF-8 BOM it reads them as Shift_JIS and
 * turns every Japanese column into mojibake. The BOM is not optional here.
 */
const BOM = '﻿';

const stamp = (iso: string): string => iso.slice(0, 19).replace(/[-:T]/g, '').slice(0, 14);

export const downloadErrorCsv = (items: NotificationItem[]): void => {
  if (items.length === 0) return;

  const name =
    items.length === 1
      ? `error_${items[0].id}_${stamp(items[0].occurredAt)}.csv`
      : `errors_${items.length}件_${stamp(new Date().toISOString())}.csv`;

  downloadBlob(new Blob([BOM + buildErrorCsv(items)], { type: 'text/csv;charset=utf-8;' }), name);
};

/**
 * エラー詳細 for a LIVE run — streamed by the server, not built here.
 *
 * The two versions carry different things on purpose. The client-side file above can only contain what the
 * row on screen holds, which for a run is ONE summary sentence; the server reads sync_audit_logs and emits
 * one line per failed rezept per attempt, with 治療院ID / 提出年月 / 受診者番号 in front so each line can be
 * looked up. Owner, 2026-08-30: 「lỗi cho người vận hành thì sẽ được download thông tin vào file csv …
 * gửi cái này cho dev」.
 *
 * FETCHED WITH THE API CLIENT rather than opened as a link: the endpoint is behind the bearer token, and an
 * <a href> carries no Authorization header — it would land on the login screen instead of a file.
 */
export const downloadRunErrorCsv = async (siteType: string, id: number): Promise<void> => {
  const response = await apiClient.get<Blob>(
    `/admin/${siteType}/notification-center/${id}/error-csv`,
    { responseType: 'blob' }
  );

  const name =
    parseFilenameFromHeader(response.headers?.['content-disposition']) ??
    `sync_errors_${id}.csv`;

  downloadBlob(response.data, name);
};

/**
 * 不一致明細CSV — the rezepts behind one 状態の不一致 notice.
 *
 * STREAMED, like downloadRunErrorCsv above and unlike downloadErrorCsv: the rows are not on the screen. The
 * notice carries a count, and the detail was written to disk by the nightly reconcile that raised it.
 */
export const downloadDriftCsv = async (siteType: string, id: number): Promise<void> => {
  const response = await apiClient.get<Blob>(
    `/admin/${siteType}/notification-center/${id}/drift-csv`,
    { responseType: 'blob' }
  );

  const name =
    parseFilenameFromHeader(response.headers?.['content-disposition']) ??
    `reconcile_drift_${id}.csv`;

  downloadBlob(response.data, name);
};
